import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { Divider, Image } from '@time-line/components/common';
import { Star } from '@time-line/components/icons';
import type { SanityImage } from '@time-line/components/types';
import {
  MULTI_PRODUCT_RECOMMEND_LABEL,
  SINGLE_PRODUCT_RECOMMEND_LABEL,
} from 'components/reviews/constants';
import type { ReactNode } from 'react';

import type { ReviewsCarouselStats } from './useReviewsCarouselStats';

interface ReviewSummaryCardProps {
  overview?: ReviewsCarouselStats;
  isMultiProduct?: boolean;
  showOverview?: boolean;
  showRecommendationScore?: boolean;
  formatThumbnail?: SanityImage;
}

function StatColumn({
  value,
  suffix,
  label,
}: {
  value: string;
  suffix: ReactNode;
  label: string;
}) {
  return (
    <VStack align="start" spacing="3xs" flex="1" minW={0}>
      <HStack align="baseline" spacing="5xs">
        <Text size="h2">{value}</Text>
        {suffix}
      </HStack>
      <Divider width="full" />
      <Text size="title-serif" sx={{ textWrap: 'pretty' }}>
        {label}
      </Text>
    </VStack>
  );
}

export function ReviewSummaryCard({
  overview,
  showOverview,
  showRecommendationScore,
  formatThumbnail,
  isMultiProduct,
}: ReviewSummaryCardProps) {
  const showAverage = showOverview && overview?.ratingAverage != null;
  const showRecommend =
    showRecommendationScore && overview?.recommendedPercentage != null;

  if (!showAverage && !showRecommend) return null;

  return (
    <Box
      position="relative"
      bg="whiteAlpha.250"
      borderRadius="base"
      py="xs"
      px="m"
      color="gray.50"
      w={{
        base: 'full',
        md: showAverage && showRecommend ? '408px' : '260px',
      }}
      flexShrink={0}
    >
      <HStack align="start" spacing="3xs">
        {showAverage && (
          <StatColumn
            value={overview!.ratingAverage!.toFixed(1)}
            suffix={
              <HStack align="center" spacing="5xs">
                <Text size="body2">/ 5</Text>
                <Star boxSize="16px" />
              </HStack>
            }
            label={
              overview?.ratingCount != null
                ? `Based on ${overview.ratingCount.toLocaleString()} reviews`
                : 'Average rating'
            }
          />
        )}
        {showRecommend && (
          <StatColumn
            value={`${Math.round(overview!.recommendedPercentage!)}`}
            suffix={<Text size="body2">%</Text>}
            label={
              isMultiProduct
                ? MULTI_PRODUCT_RECOMMEND_LABEL
                : SINGLE_PRODUCT_RECOMMEND_LABEL
            }
          />
        )}
      </HStack>

      {formatThumbnail && (
        <Box position="absolute" top="-40px" right="8px" h="120px" w="120px">
          <Image
            image={formatThumbnail}
            fill
            builderOptions={{ fill: true }}
            alt={formatThumbnail.alt ?? ''}
            sizes="120px"
            style={{ objectFit: 'contain' }}
          />
        </Box>
      )}
    </Box>
  );
}
